import { motion } from 'framer-motion';
import { styled, css } from 'styled-components';
import { Media } from '../../../../../theme/Media.js';
import { TABLE_ROW_EXPANDING_DURATION_MS } from '../../constants.js';
import { TableRowRole } from './types.js';
export const TableBody = styled.tbody `
  display: table-row-group;
  transition: border-radius ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;
  position: relative;

  & * {
    transition: border-radius ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 3px solid ${({ theme }) => theme.colors.mainLight};
    border-radius: 8px;
    pointer-events: none;
    // Above the group marker
    z-index: 4;
    opacity: 0;
    transition: opacity ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;
  }

  &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
    &::before {
      content: '';
      position: absolute;
      // Above a sticky CHECKBOX first cell, which would hide the marker otherwise
      z-index: 3;
      top: 0;
      bottom: 0;
      left: 0;
      width: 8px;
      background: ${({ theme }) => theme.colors.lightBlue30};
    }
  }

  &[data-is-expanded='true'] {
    border-radius: 8px;

    &::after {
      opacity: 1;
    }
  }

  @media ${Media.smMin} {
    // Visual separator between consecutive rows
    // Applies top border to first TR of DEFAULT/GROUP_ELEMENT rows when stacked
    &[data-row-role='${TableRowRole.DEFAULT}'] + &[data-row-role='${TableRowRole.DEFAULT}'],
    &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] + &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
      & > tr:first-child td {
        border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
      }
    }

    // First GROUP_ELEMENT after GROUP_HEADER
    // Rounds top-right corners of both the TR and its last TD
    &[data-row-role='${TableRowRole.GROUP_HEADER}'] + &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
      & > tr:first-child {
        border-top-right-radius: 8px;

        & > td:last-child {
          border-top-right-radius: 8px;
        }
      }
    }

    // GROUP_ELEMENT before SPACER (end of group)
    // Applies bottom rounding to:
    // - The component's last TR
    // - First/last TDs in that TR
    // - ::before pseudo-element (group marker)
    // Note: First TR remains unrounded for visual continuity
    &[data-row-role='${TableRowRole.GROUP_ELEMENT}']:has(+ &[data-row-role='${TableRowRole.SPACER}']) {
      & > tr {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;

        &:first-child {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        & > td:first-child {
          border-bottom-left-radius: 8px;
        }

        & > td:last-child {
          border-bottom-right-radius: 8px;
        }
      }

      &::before {
        border-bottom-left-radius: 8px;
      }
    }

    // Expanded DEFAULT row after SPACER
    // Rounds top corners of first TR and its cells
    // (Used when expanding content after a spacer)
    &[data-row-role='${TableRowRole.SPACER}'] + &[data-row-role='${TableRowRole.DEFAULT}'][data-is-expanded='true'] {
      & > tr:first-child {
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;

        & > td:first-child {
          border-top-left-radius: 8px;
        }

        & > td:last-child {
          border-top-right-radius: 8px;
        }
      }
    }

    // Expanded DEFAULT row that precedes SPACER
    // Rounds bottom corners of last TR and its cells
    // (Used when expanded content appears above a spacer)
    &[data-row-role='${TableRowRole.DEFAULT}'][data-is-expanded='true']:has(+ &[data-row-role='${TableRowRole.SPACER}']) {
      & > tr:last-child {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;

        & > td:first-child {
          border-bottom-left-radius: 8px;
        }

        & > td:last-child {
          border-bottom-right-radius: 8px;
        }
      }
    }
  }

  @media ${Media.xsMax} {
    border-radius: 8px;
    overflow: hidden;

    // Every cell is a line of its own here, so the group marker gets room beside all of them. The table is a
    // flex column on mobile, which makes this tbody a block box that can take padding
    &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
      padding-left: 8px;
    }

    // Group header should have flat bottom corners
    &[data-row-role='${TableRowRole.GROUP_HEADER}'] {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    // First group element after header should have flat top-left corner
    &[data-row-role='${TableRowRole.GROUP_HEADER}'] + &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
      border-top-left-radius: 0;
    }

    // Add spacing between consecutive default rows or group elements
    &[data-row-role='${TableRowRole.DEFAULT}'] + &[data-row-role='${TableRowRole.DEFAULT}'],
    &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] + &[data-row-role='${TableRowRole.GROUP_ELEMENT}'] {
      margin-top: 8px;
    }
  }
`;
export const TableRowWrapper = styled.tr `
  position: relative;

  & > td {
    background: ${({ theme }) => theme.colors.grey00_1};
  }

  &[data-row-role='${TableRowRole.GROUP_HEADER}'] > td,
  &[data-row-role='${TableRowRole.SPACER}'] > td {
    background: transparent;
  }

  ${({ theme, $isInteractive }) => $isInteractive &&
    css `
      cursor: pointer;
      &:hover {
        & > td {
          background: ${theme.colors.grey01_1};

          & > * {
            background: ${theme.colors.grey01_1};
          }
        }
      }
    `}

  ${({ theme, $flash }) => $flash &&
    css `
      & > td {
        animation: highlight-row 4s linear;

        & > * {
          animation: highlight-row 4s linear;
        }
      }

      @keyframes highlight-row {
        0% {
          background: ${theme.colors.grey00_1};
        }
        10% {
          background: ${theme.colors.grey01_1};
        }
        75% {
          background: ${theme.colors.grey01_1};
        }
        100% {
          background: ${theme.colors.grey00_1};
        }
      }
    `}

  ${({ theme, $highlighted }) => $highlighted &&
    css `
      & > td {
        background: ${theme.colors.grey01_1};

        & > * {
          background: ${theme.colors.grey01_1};
        }
      }
    `}

  @media ${Media.xsMax} {
    display: block;
  }
`;
export const ExpandableTableRow = styled.tr `
  @media ${Media.xsMax} {
    display: block;
  }
`;
export const ExpandableTableCell = styled.td `
  padding: 0;
  position: relative;
  transition: border-radius ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.grey10_1};
  }

  @media ${Media.xsMax} {
    display: block;
  }
`;
export const ExpandableTableCellMotion = styled(motion.div) `
  overflow: hidden;
`;
export const ExpandableTableCellMotionInner = styled.div `
  padding: 8px 10px 10px;
  background: ${({ theme }) => theme.colors.grey01_1};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;
